import { Component, OnDestroy, inject, Inject } from '@angular/core';
import { RegistrationService, PrecheckResponse, ClientType } from '@brightside-web/desktop/data-access/onboarding';
import { AxiosError } from 'axios';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { ActivatedRoute, Router } from '@angular/router';

import {
  BsCacheService
} from '@brightside-web/desktop/data-access/core-services';

import { Subscription } from 'rxjs';
import {
  ApiResponse,
  AuthSsoService,
  InviteService,
  FirebaseService,
  CompanyService
} from '@brightside-web/desktop/data-access/shared';
import { EMAIL_PATTERN } from '@brightside-web/shared/desktop';
import { Environment } from '@brightside-web/micro/core/environment';

import { CONTROL_LAST_FOUR } from "@brightside-web/shared/desktop";

@Component({ template: '' })
export class IdentityVerification implements OnDestroy {

  protected bsCacheService = inject(BsCacheService);
  protected env = inject(Environment);
  protected registrationService = inject(RegistrationService);
  protected analytics = inject(FirebaseService);
  protected router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);
  protected companyService = inject(CompanyService);

  public signUpInputForm: FormGroup;
  error: boolean;
  processing: boolean;
  sub = new Subscription();
  email_pattern = EMAIL_PATTERN;
  signInModalVisible: boolean;
  eligibilityError: boolean;

  constructor(
  ) {
    this.signUpInputForm = new FormGroup({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern(new RegExp(this.email_pattern))
      ]),
      firstName: new FormControl('', [
        Validators.required,
        Validators.minLength(2)]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.minLength(2)]),
      last4ssn: new FormControl('', [
        Validators.required,
        Validators.maxLength(4),
        Validators.minLength(4),
        Validators.pattern('(\\d{4})'),
      ]),
    });
    const body = document.getElementsByTagName('body')[0];
    body.classList.add('signup');
  }

  precheck(): void {
    //Clear any household dep info
    InviteService.clearCacheHousehold();
    if (this.signUpInputForm.valid) {
      this.processing = true;
      this.sub.add(
        this.registrationService
          .newRegistrationPrecheck({
            first_name: this.signUpInputForm.controls['firstName'].value,
            last_name: this.signUpInputForm.controls['lastName'].value,
            last_four_ssn: this.signUpInputForm.controls['last4ssn'].value,
            date_of_birth: this.transformDob(),
          })
          .subscribe(
            (resp: PrecheckResponse) => {
              this.processing = false;
              this.handleResponse(resp);
            },
            (err: AxiosError) => {
              this.processing = false;
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              this.handleResponse(typeof err?.response?.data === 'object' ? err.response.data : {});
            }
          )
      );
    }
  }

  protected handleResponse(resp: PrecheckResponse): void {
    switch (resp.statusCode) {
      case 200:
        this.handle200(resp.company, resp.clientType);
        break;
      case 412:
        this.handle412();
        break;
      case 403:
        this.handle403();
        break;
      case 404:
        this.handle404();
        break;
      case 402:
        this.handle402();
        break;
      default:
        this.handleError(resp);
        break;
    }

  }

  private transformDob(): string {
    let output = '';
    const input = this.signUpInputForm.controls['dateOfBirth'] ? this.signUpInputForm.controls['dateOfBirth'].value : undefined;

    if (input) {
      output = moment(input, 'MM/DD/YYYY').format('YYYY-MM-DD');
      // some of the date inputs provide correct format
      if (output.toLowerCase() === 'invalid date') {
        output = input;
      }
    }
    return output;
  }

  handleError(resp: ApiResponse) {
    this.error = true;

    if (resp.statusCode === 409) {
      this.analytics.logEvent('error_shown', {
        'error id': 'invalid_identity_modal'
      });
    } else {
      this.analytics.logEvent('error_shown', {
        'error id': 'invalid_identity_modal',
      });
    }
  }

  handle412() {
    this.signInModalVisible = true;
  }

  protected cacheSignupForm() {
    this.bsCacheService.setItem('firstName', this.signUpInputForm.controls['firstName'].value);
    this.bsCacheService.setItem('lastName', this.signUpInputForm.controls['lastName'].value);
    this.bsCacheService.setItem('last4ssn', this.signUpInputForm.controls['last4ssn'].value);
    this.bsCacheService.setItem('email', this.signUpInputForm.controls['email'].value);
  }

  handle200(company: string, clientType: ClientType) {
    this.bsCacheService.setItem('create-account', 'allowed');
    this.cacheSignupForm();
    this.bsCacheService.setItem('dob', this.transformDob());
    this.companyService.setCompany(company);
    this.bsCacheService.setItem('clientType', clientType);

    // const redirect = AuthSsoService.getRedirectTo(this.env.oauth,company);
    // if (redirect && clientType === ClientType.Primary) {
    //   window.location.assign(redirect);
    //   return;
    // }

    this.router.navigate(['..', 'create-account'], { relativeTo: this.activatedRoute });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  signIn() {
    RegistrationService.clearOnboardingCache();
    this.router.navigate(['/sign_in']);
  }

  private handle402() {
    this.eligibilityError = true;
  }

  private handle404() {
    InviteService.preCacheHousehold({
      fName: this.signUpInputForm.controls['firstName'].value,
      lName: this.signUpInputForm.controls['lastName'].value,
      dateOfBirth: this.transformDob(),
      lastFourSSN: this.signUpInputForm.controls['last4ssn'].value,
      email: this.signUpInputForm.controls['email'].value,
    });
    this.router.navigate(['registration', 'discover', 'employer']);
  }

  private handle403() {

    InviteService.preCacheHousehold({
      fName: this.signUpInputForm.controls['firstName'].value,
      lName: this.signUpInputForm.controls['lastName'].value,
      dateOfBirth: '',
      lastFourSSN: this.signUpInputForm.controls['last4ssn'].value,
      email: this.signUpInputForm.controls['email'].value,
    });


    // Verify birthday guard wants these items from cache
    this.bsCacheService.setItem('firstName', this.signUpInputForm.controls['firstName'].value);
    this.bsCacheService.setItem('lastName', this.signUpInputForm.controls['lastName'].value);
    this.bsCacheService.setItem('last4ssn', this.signUpInputForm.controls['last4ssn'].value);
    this.bsCacheService.setItem('email', this.signUpInputForm.controls['email'].value);

    this.router.navigate(['registration', 'birthday']);
  }
}
